import { Divider, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import StyledCard from 'src/components/CheckoutV2/shared/StyledCard';
import { FunnelCart } from 'src/ts/types/cart';
import { centsToDollarsStr } from 'src/utils/currency';
import { useDiscount } from 'src/context/discount';
import DiscountCodeRow from './DiscountCodeRow';

interface Props {
  cart: FunnelCart;
  setCart: React.Dispatch<React.SetStateAction<FunnelCart | null>>;
  cartLedger: {
    subtotal: number;
    deliveryTotal: number;
    discountTotal: number;
    precreditTotal: number;
    creditTotal: number;
    tax: number;
    grandTotal: number;
  };
  totalNumMeals: number;
  validationError?: NonNullable<FunnelCart['cartError']>['validation'];
}

const MenuPriceSummary: React.FC<Props> = ({
  cart,
  setCart,
  cartLedger,
  totalNumMeals,
  validationError,
}) => {
  const discountEligibility = useDiscount();
  const theme = useTheme();

  // Used to show the "Please check your meal quantites" copy.
  const invalidCart =
    (validationError?.quantity && validationError?.quantity.length > 0) ||
    totalNumMeals === 0;

  // This is used to add bullet points to the errors in case there's multiple. Otherwise
  // it's hard to visually differentiate the errors
  const multipleErrors =
    invalidCart && discountEligibility.eligibleDiscount?.totalMealMinimumCopy;

  return (
    <StyledCard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body1">Subtotal</Typography>
          <Typography variant="body1">
            {centsToDollarsStr(cartLedger.subtotal)}
          </Typography>
        </Box>
        {!!cartLedger.creditTotal && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1">Credits</Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.error.light,
                fontWeight: 'bold',
              }}
            >
              -{centsToDollarsStr(cartLedger.creditTotal)}
            </Typography>
          </Box>
        )}
        <DiscountCodeRow
          cart={cart}
          setCart={setCart}
          discountTotal={cartLedger.discountTotal}
        />
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body1">Total</Typography>
          <Typography variant="body1">
            {/* 
            This subtraction is being done because we don't want to show any delivery-related
            costs on the menu page and the API doesn't provide any data point on the ledger that's
                          (sum of item cost) - discounts - credits
            The grandTotal always also includes delivery charges.
          */}
            {centsToDollarsStr(
              cartLedger.grandTotal - cartLedger.deliveryTotal,
            )}
          </Typography>
        </Box>
      </Box>
      {discountEligibility.eligibleDiscount?.totalMealMinimumCopy && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            fontWeight: 'bold',
          }}
        >
          {multipleErrors ? '• ' : undefined}
          {discountEligibility.eligibleDiscount?.totalMealMinimumCopy}
        </Typography>
      )}
      {invalidCart && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            fontWeight: 'bold',
          }}
        >
          {multipleErrors ? '• ' : undefined}Please check your meal quantities
        </Typography>
      )}
    </StyledCard>
  );
};

export default MenuPriceSummary;
